import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    AuthApiResponse,
    ForgotPasswordRequestDto,
    LoginCredentials,
    RefreshTokenRequestDto,
    RefreshTokenResponseDto,
    RegistrationRequestDto,
    RegistrationResponseDto,
    ResendValidationTokenRequestDto,
    ResetPasswordRequestDto,
    UpdatePasswordRequestDto,
    UserLoginResponse,
    VerifyUserAccountDto,
} from './auth.model';
import { LoggerService } from '../services/logger.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AuthApiService {
    private http = inject(HttpClient);
    private loggerService = inject(LoggerService);
    private baseUrl = `${environment.API_URL}/auth`;
    private loginUrl = `${this.baseUrl}/login`;
    private registerUrl = `${this.baseUrl}/register`;
    /**
     * Sends login credentials to the backend.
     * @param credentials User's email and password.
     * @returns An Observable of the UserLoginResponse from the backend.
     */
    login(credentials: LoginCredentials): Observable<UserLoginResponse> {
        return this.http.post<AuthApiResponse<UserLoginResponse>>(this.loginUrl, credentials).pipe(
            map((response: AuthApiResponse<UserLoginResponse>) => response.data),
            catchError(error => {
                this.loggerService.error('AuthApiService: login error', error);
                // Re-throw the error for the store to handle
                return throwError(() => error);
            })
        );
    }

    /**
     * Sends registration request to the backend.
     * @param payload The registration data.
     * @returns An Observable of the RegistrationResponseDto from the backend.
     */
    register(payload: RegistrationRequestDto): Observable<RegistrationResponseDto> {
        //  backend returns RegistrationResponseDto directly
        return this.http.post<RegistrationResponseDto>(this.registerUrl, payload).pipe(
            catchError(error => {
                this.loggerService.error('AuthApiService: register error', error);
                // Re-throw the error for the store to handle
                return throwError(() => error);
            })
        );
    }

    /**
     * Verifies user account with a token and email.
     * Corresponds to POST /api/auth/verify
     * @param payload The verification data (email and token).
     * @returns An Observable of AuthApiResponse<Object> (or whatever the backend returns for success).
     */
    verifyAccount(payload: VerifyUserAccountDto): Observable<AuthApiResponse<Object>> {
        return this.http.post<AuthApiResponse<Object>>(`${this.baseUrl}/verify`, payload).pipe(
            catchError(error => {
                this.loggerService.error('AuthApiService: verifyAccount error', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Requests a password reset link for the given email.
     * Corresponds to POST /api/auth/forgot-password
     * @param payload The email for password reset.
     * @returns An Observable of string (message from backend).
     */
    forgotPassword(payload: ForgotPasswordRequestDto): Observable<string> {
        return this.http
            .post(`${this.baseUrl}/forgot-password`, payload, { responseType: 'text' })
            .pipe(
                catchError(error => {
                    this.loggerService.error('AuthApiService: forgotPassword error', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Resets the user's password using a valid token.
     * Corresponds to POST /api/auth/reset-password
     * @param payload The reset token and new password.
     * @returns An Observable of string (message from backend).
     */
    resetPassword(payload: ResetPasswordRequestDto): Observable<string> {
        return this.http
            .post(`${this.baseUrl}/reset-password`, payload, { responseType: 'text' })
            .pipe(
                catchError(error => {
                    this.loggerService.error('AuthApiService: resetPassword error', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Resends the email validation token.
     * Corresponds to POST /api/auth/resend-validation-code
     * @param payload The email to resend token to.
     * @returns An Observable of any (backend returns `ResponseEntity<?>`).
     */
    resendValidationToken(payload: ResendValidationTokenRequestDto): Observable<any> {
        return this.http.post(`${this.baseUrl}/resend-validation-code`, payload).pipe(
            catchError(error => {
                this.loggerService.error('AuthApiService: resendValidationToken error', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Updates the current authenticated user's password.
     * Corresponds to PUT /api/auth/update-password
     * @param payload The current and new passwords.
     * @returns An Observable of string (message from backend).
     */
    updatePassword(payload: UpdatePasswordRequestDto): Observable<string> {
        return this.http
            .put(`${this.baseUrl}/update-password`, payload, { responseType: 'text' })
            .pipe(
                catchError(error => {
                    this.loggerService.error('AuthApiService: updatePassword error', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Refreshes access and refresh tokens.
     * Corresponds to POST /api/auth/refresh-token
     * @param payload The refresh token.
     * @returns An Observable of RefreshTokenResponseDto.
     */
    refreshToken(payload: RefreshTokenRequestDto): Observable<RefreshTokenResponseDto> {
        return this.http
            .post<RefreshTokenResponseDto>(`${this.baseUrl}/refresh-token`, payload)
            .pipe(
                catchError(error => {
                    this.loggerService.error('AuthApiService: refreshToken error', error);
                    return throwError(() => error);
                })
            );
    }
}
